import {isBankUser, isUnitUser, useAccessToken, useUserType} from "../../services/auth"
import {usePaging} from "../../hooks/usePaging"
import {ErrorDocument} from "../../resources/common"
import {findWalletTerms} from "../../resources/walletTerms"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import React, {useEffect, useMemo} from "react"
import moment from "moment"
import {useAsyncResult} from "../../hooks/useAsyncResult"
import {useQueryState} from "use-location-state"
import {findOrgs, Org} from "../../resources/org"
import {AsyncResult} from "../../types/asyncResult"
import {Filter} from "../Filter/Filter"
import {OrgFilter} from "../OrgFilter/OrgFilter"
import SearchBox from "../../components/SearchBox/SearchBox"
import {DataTableActionHeader, DataTableCard, TablePending} from "../DataTable/DataTable"
import {Bank, findBanks} from "../../resources/bank"
import {AllowedWalletsTermsColumns} from "./WalletTerms.types"
import WalletTermsTable from "./WalletTermsTable"

interface WalletsTermsProps {
	token?: string
	limit?: number
	includedColumns: AllowedWalletsTermsColumns[]
	enableTitle?: boolean
	fullHeight?: boolean
	enableOrgFilter?: boolean
}

export default function WalletsTerms({limit = 25, enableTitle, fullHeight, includedColumns}: WalletsTermsProps) {
	const prefix = "wt"
	const accessToken = useAccessToken()
	const userType = useUserType()
	const [filteredOrgs, setFilteredOrgs] = useQueryState<string[]>("filter[orgs]", [])

	const [filteredBanks, setFilteredBanks] = useQueryState<string[]>(`${prefix}-filter[banks]`, [])
	const [query, setQuery] = useQueryState("filter[query]", "")
	const [result, hasPrev, hasNext, prev, next, hasResults, reset] = usePaging(
		limit,
		(offset, limit) => findWalletTerms(accessToken, offset, limit, filteredOrgs, filteredBanks, query),
		(x) => x.data.length,
		[filteredOrgs.join(","), filteredBanks.join(","), query]
	)

	const orgs =
		isUnitUser(userType) || isBankUser(userType)
			? useAsyncResult(() => findOrgs(accessToken, 0, 10000), [])
			: AsyncResult.pending<Org[], ErrorDocument>()
	const orgFilter = useMemo(
		() => OrgFilter(orgs, setFilteredOrgs, filteredOrgs, reset, limit),
		[orgs, setFilteredOrgs, filteredOrgs.join(","), reset, limit]
	)

	const banks =
		userType === "unit"
			? useAsyncResult(() => findBanks(accessToken, 0, 10000), [])
			: AsyncResult.pending<Bank[], ErrorDocument>()

	const bankFilter = banks.match(
		() => null,
		(banks) => (
			<Filter
				title="Banks"
				isSearchable
				setStatuses={setFilteredBanks}
				statuses={filteredBanks}
				onFilterFunc={() => reset(limit)}
				options={
					new Map<string, string>(
						banks
							.sort((a, b) => moment(b.attributes.createdAt).diff(moment(a.attributes.createdAt)))
							.map((bank) => [bank.id, bank.attributes.name])
					)
				}
			/>
		),
		(_) => null
	)

	const [searchBox, setIsSearchLoading] = SearchBox(query, "Search Wallet Terms", 500, onSearch)
	useEffect(() => {
		result.match(
			() => null,
			() => setIsSearchLoading(false),
			() => setIsSearchLoading(false)
		)
	}, [result])

	function onSearch(searchTerm: string) {
		setQuery(searchTerm)
		reset(limit)
	}

	return (
		<DataTableCard className={"payments-card"}>
			<DataTableActionHeader
				enableSticky={fullHeight}
				searchBox={searchBox}
				filters={[orgFilter, bankFilter]}
				title={enableTitle ? "Wallet Terms" : null}
			/>
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: walletsTerms}) => {
					return (
						<WalletTermsTable
							walletsTerms={walletsTerms.data}
							meta={walletsTerms.meta}
							hasResults={hasResults}
							hasPrev={hasPrev}
							hasNext={hasNext}
							prev={prev}
							next={next}
							isUsingPaging={true}
							include={walletsTerms.included}
							fullHeight={fullHeight}
							includedColumns={includedColumns}
						/>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
